/**
 * Execution Event Stream — pure normalizer
 * ─────────────────────────────────────────
 * Observational read-only projection over canonical sinks. No writers,
 * no telemetry, no new persistence. Used by the Execution domain rail
 * to give operators passive situational awareness.
 *
 * Sources (canonical, untouched):
 *   • Callbacks                  (callbacks)
 *   • Holdback ledger            (holdbackLedger)
 *   • Field material feedback    (fieldMaterialFeedback — VendorPOContext)
 *   • Substitution requests      (substitutionRequests   — VendorPOContext)
 *   • Phase executions           (specExecutions — phase.completedAt)
 *
 * Hard rules:
 *   • Pure function. No React. No mutations.
 *   • Never carries financial or personal data.
 *   • Sort: most recent first, ties broken by stable id.
 */

import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { HoldbackLedgerEntry } from "@/data/contractor/networkExecution/holdback/ledger";
import type {
  FieldMaterialFeedbackEvent,
  SubstitutionRequest,
} from "@/data/VendorPOContext";
import type { JobSpecExecution } from "@/data/jobSpecExecution";

export type ExecutionEventCategory =
  | "qa"
  | "field"
  | "scheduling"
  | "issues";

export type ExecutionEventKind =
  | "callback_created"
  | "holdback_action"
  | "field_material_issue"
  | "substitution_request"
  | "phase_submitted"
  | "phase_qa_passed"
  | "phase_qa_failed";

export interface ExecutionEvent {
  id: string;
  kind: ExecutionEventKind;
  category: ExecutionEventCategory;
  jobId: string;
  /** ISO timestamp. */
  at: string;
  /** Short headline shown in the rail (single line). */
  headline: string;
  /** Optional sub-line: subject / phase / type. */
  detail?: string;
  /** Optional actor / source tag. */
  actor?: string;
}

export type ExecutionEventFilter = "all" | ExecutionEventCategory;

/* ── normalizers (one per source) ─────────────────────────────────────── */

function fromCallbacks(callbacks: Callback[]): ExecutionEvent[] {
  return callbacks.map((c) => ({
    id: `cb:${c.id}`,
    kind: "callback_created",
    category: c.type === "leak" || c.type === "workmanship" ? "qa" : "issues",
    jobId: c.jobId,
    at: c.filedAt,
    headline: `Callback — ${c.type.replace(/_/g, " ")}`,
    detail: c.severity ? `${c.severity} severity` : undefined,
    actor: c.filedBy,
  }));
}

function fromHoldbacks(entries: HoldbackLedgerEntry[]): ExecutionEvent[] {
  return entries.map((h) => ({
    id: `hb:${h.id}`,
    kind: "holdback_action",
    category: "issues",
    jobId: h.jobId,
    at: h.createdAt,
    headline: `Holdback ${h.action}`,
    detail: h.reason ? truncate(h.reason, 60) : undefined,
    actor: h.actorId,
  }));
}

function fromFieldFeedback(
  events: FieldMaterialFeedbackEvent[],
): ExecutionEvent[] {
  return events.map((e) => ({
    id: `ff:${e.id}`,
    kind: "field_material_issue",
    category: "field",
    jobId: e.jobId,
    at: e.createdAt,
    headline: `Field material issue — ${e.issueType.replace(/_/g, " ")}`,
    detail: truncate(e.description ?? "", 60) || undefined,
    actor: e.actor,
  }));
}

function fromSubstitutions(reqs: SubstitutionRequest[]): ExecutionEvent[] {
  return reqs.map((r) => ({
    id: `sb:${r.id}`,
    kind: "substitution_request",
    category: "field",
    jobId: r.jobId,
    at: r.createdAt,
    headline: `Substitution requested`,
    detail: truncate(r.proposedDescription ?? r.reason, 60),
    actor: r.createdBy,
  }));
}

function fromPhases(executions: JobSpecExecution[]): ExecutionEvent[] {
  const out: ExecutionEvent[] = [];
  for (const exec of executions) {
    for (const phase of exec.phases) {
      if (!phase.completedAt) continue;
      if (phase.status === "submitted") {
        out.push({
          id: `ph:${exec.jobId}:${phase.phaseName}:submitted`,
          kind: "phase_submitted",
          category: "qa",
          jobId: exec.jobId,
          at: phase.completedAt,
          headline: `Phase submitted — ${phase.phaseName}`,
        });
      } else if (phase.status === "qa_passed") {
        out.push({
          id: `ph:${exec.jobId}:${phase.phaseName}:passed`,
          kind: "phase_qa_passed",
          category: "qa",
          jobId: exec.jobId,
          at: phase.completedAt,
          headline: `QA passed — ${phase.phaseName}`,
        });
      } else if (phase.status === "qa_failed") {
        out.push({
          id: `ph:${exec.jobId}:${phase.phaseName}:failed`,
          kind: "phase_qa_failed",
          category: "qa",
          jobId: exec.jobId,
          at: phase.completedAt,
          headline: `QA failed — ${phase.phaseName}`,
        });
      }
    }
  }
  return out;
}

/* ── public API ───────────────────────────────────────────────────────── */

export interface BuildExecutionEventStreamInput {
  callbacks: Callback[];
  holdbackLedger: HoldbackLedgerEntry[];
  fieldMaterialFeedback: FieldMaterialFeedbackEvent[];
  substitutionRequests: SubstitutionRequest[];
  specExecutions: JobSpecExecution[];
}

export function buildExecutionEventStream(
  input: BuildExecutionEventStreamInput,
): ExecutionEvent[] {
  const merged = [
    ...fromCallbacks(input.callbacks),
    ...fromHoldbacks(input.holdbackLedger),
    ...fromFieldFeedback(input.fieldMaterialFeedback),
    ...fromSubstitutions(input.substitutionRequests),
    ...fromPhases(input.specExecutions),
  ];
  // Most recent first; stable tiebreak on id.
  merged.sort((a, b) => {
    if (a.at === b.at) return a.id.localeCompare(b.id);
    return a.at < b.at ? 1 : -1;
  });
  return merged;
}

export function filterExecutionEvents(
  events: ExecutionEvent[],
  filter: ExecutionEventFilter,
): ExecutionEvent[] {
  if (filter === "all") return events;
  return events.filter((e) => e.category === filter);
}

/* ── helpers ──────────────────────────────────────────────────────────── */

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

export function formatRelativeTime(atIso: string, nowMs: number): string {
  const then = Date.parse(atIso);
  if (Number.isNaN(then)) return "—";
  const deltaSec = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
